// Canonical frontend track-policy facade. **This module is the only
// frontend API for regulatory-track decisions.** Components must
// import from here; they must NOT:
//   * call regulation_used.includes(...) inline
//   * read ui/src/services/regulation_policy_matrix.json directly
//   * roll their own normalize() helper
//
// The JSON file is implementation data only — its shape is the
// contract the backend ships at the boundary; this module is the
// runtime contract for consumers.
//
// Backend mirror: app/services/regulation_policy_matrix.py.
// State-model authority: docs/compliance/regcf-506c-state-model.md §1, §4.
// Implementation plan: docs/compliance/regcf-506c-implementation-plan.md §7.7 (commit 2).

const { offeringCloseInstant } = require('../utils/offeringClose');

const POLICY = require('./regulation_policy_matrix.json');

const REG_CF_SPELLINGS = new Set(['reg_cf', 'Reg CF']);
const RULE_506C_SPELLINGS = new Set(['506c', 'rule_506c', 'Rule 506(c)']);

const TRACK_REG_CF = 'reg_cf';
const TRACK_RULE_506C = 'rule_506c';

function _name_of(entry) {
  if (typeof entry === 'string') return entry;
  return entry && entry.name;
}

function normalize(raw) {
  const name = _name_of(raw);
  if (!name) return null;
  if (REG_CF_SPELLINGS.has(name)) return TRACK_REG_CF;
  if (RULE_506C_SPELLINGS.has(name)) return TRACK_RULE_506C;
  return null;
}

function tracks_for_home(home) {
  const used = (home && home.regulation_used) || [];
  const tracks = new Set();
  for (const entry of used) {
    const t = normalize(entry);
    if (t) tracks.add(t);
  }
  return tracks;
}

function has_reg_cf(home) { return tracks_for_home(home).has(TRACK_REG_CF); }
function has_506c(home) { return tracks_for_home(home).has(TRACK_RULE_506C); }

function is_mixed(home) {
  const t = tracks_for_home(home);
  return t.has(TRACK_REG_CF) && t.has(TRACK_RULE_506C);
}
function is_only_506c(home) {
  const t = tracks_for_home(home);
  return t.has(TRACK_RULE_506C) && !t.has(TRACK_REG_CF);
}
function is_only_reg_cf(home) {
  const t = tracks_for_home(home);
  return t.has(TRACK_REG_CF) && !t.has(TRACK_RULE_506C);
}

function allowed_tracks(home) {
  return Array.from(tracks_for_home(home));
}

function _config_for_track(home, track) {
  const canonical = normalize(track);
  if (!canonical || !home) return null;
  return ((home.economic_track_configs || []).find(
    (cfg) => normalize(cfg.regulation_track) === canonical
  ) || null);
}

// Owner decision 2026-10-20: the close is the end of the stored date in
// Eastern time (utils/offeringClose.js, as the server's is_track_open).
function _open_from_funding_round_end(value) {
  if (!value) return null;
  const closeAt = offeringCloseInstant(value);
  if (!closeAt) return null;
  return closeAt.getTime() > Date.now();
}

function _mixed_track_close_state(home, track) {
  if (!is_mixed(home)) return null;
  const canonical = normalize(track);
  if (!canonical || !allowed_tracks(home).includes(canonical)) return false;
  const cfg = _config_for_track(home, canonical);
  if (!cfg) return false;
  const open = _open_from_funding_round_end(cfg.funding_round_end);
  return open === null ? false : open;
}

// Owner rule 2026-10-16: a track of a live listing with no subscription
// agreement is not open yet (the server lists it in agreement_blocked_tracks);
// the other track of a dual-track listing stays investable.
function is_agreement_blocked(home, canonical) {
  return !!(
    home &&
    Array.isArray(home.agreement_blocked_tracks) &&
    home.agreement_blocked_tracks.map(normalize).filter(Boolean).includes(canonical)
  );
}

function track_availability(home, track) {
  const canonical = normalize(track);
  if (!canonical || !home) return null;
  if (is_agreement_blocked(home, canonical)) {
    const blocked = home.track_availability && home.track_availability[canonical];
    return { ...(blocked || {}), open: false, status_label: "Not open yet" };
  }
  const explicit = home.track_availability && home.track_availability[canonical]
    ? home.track_availability[canonical]
    : null;
  if (Array.isArray(home.open_tracks)) {
    const open = home.open_tracks.map(normalize).filter(Boolean).includes(canonical);
    return {
      ...(explicit || {}),
      open,
      status_label: explicit && explicit.status_label
        ? explicit.status_label
        : (open ? "" : "Closed"),
    };
  }
  const mixedOpen = _mixed_track_close_state(home, canonical);
  if (mixedOpen !== null) {
    const explicitOpen = explicit && (
      explicit.open === true || explicit.open === false
    )
      ? explicit.open
      : mixedOpen;
    return {
      ...(explicit || {}),
      open: explicitOpen,
      status_label: explicit && explicit.status_label
        ? explicit.status_label
        : (explicitOpen ? "" : "Closed"),
    };
  }
  return explicit;
}

function open_tracks(home) {
  if (home && Array.isArray(home.open_tracks)) {
    return home.open_tracks.map(normalize).filter(Boolean)
      .filter((track) => !is_agreement_blocked(home, track));
  }
  return allowed_tracks(home).filter((track) => is_track_open(home, track));
}

function is_track_open(home, track) {
  const canonical = normalize(track);
  if (!canonical) return false;
  if (is_agreement_blocked(home, canonical)) return false;
  if (home && Array.isArray(home.open_tracks)) {
    return home.open_tracks.map(normalize).filter(Boolean).includes(canonical);
  }
  const availability = track_availability(home, track);
  if (availability && availability.open === false) return false;
  if (availability && availability.open === true) return true;
  const mixedOpen = _mixed_track_close_state(home, canonical);
  if (mixedOpen !== null) return mixedOpen;
  return allowed_tracks(home).includes(canonical);
}

// Per-track close date, mirroring the backend single source of truth
// (app/services/home_economics.funding_round_end_for_track): a single-track
// offering reads the legacy home.funding_round_end (a per-track config row is
// only a fallback); a mixed offering reads the per-track config date. Used to
// decide whether one offering-level countdown can stand in for all tracks.
function close_date_for_track(home, track) {
  const canonical = normalize(track);
  if (!home || !canonical) return null;
  if (is_mixed(home)) {
    const cfg = _config_for_track(home, canonical);
    return cfg && cfg.funding_round_end ? cfg.funding_round_end : null;
  }
  if (home.funding_round_end) return home.funding_round_end;
  const cfg = _config_for_track(home, canonical);
  return cfg && cfg.funding_round_end ? cfg.funding_round_end : null;
}

function track_status_label(home, track) {
  const availability = track_availability(home, track);
  if (availability && availability.status_label) return availability.status_label;
  if (availability && availability.open === false) return "Closed";
  return "";
}

function track_availability_map(home) {
  if (
    !home ||
    (
      !Array.isArray(home.open_tracks) &&
      !home.track_availability &&
      !is_mixed(home)
    )
  ) return {};
  const tracks = new Set([
    ...allowed_tracks(home),
    ...((home.open_tracks || []).map(normalize).filter(Boolean)),
    ...Object.keys(home.track_availability || {}).map(normalize).filter(Boolean),
  ]);
  return Array.from(tracks).reduce((acc, track) => {
    const availability = track_availability(home, track);
    if (availability) acc[track] = availability;
    return acc;
  }, {});
}

function policy_for_track(track) {
  const canonical = normalize(track);
  if (!canonical) return null;
  return POLICY[canonical] || null;
}

function user_facing_role_copy(track) {
  const policy = policy_for_track(track);
  return policy && policy.user_facing_role_copy ? policy.user_facing_role_copy : '';
}

// Per-track role-disclosure clause (compliance-required copy). A Reg CF funding
// portal *is* the intermediary; for 506(c) Invown is NOT an intermediary/broker,
// only a technology provider — this is a legal role distinction.
// COMPLIANCE-REVIEW-REQUIRED: per-track role disclosure copy.
// Single source of truth so the listings tile, RailAttribution, and any future
// surface emit the identical clause instead of re-authoring the strings.
const REG_CF_ROLE_CLAUSE = 'through Invown Funding Portal LLC as intermediary';
const RULE_506C_ROLE_CLAUSE = 'with Invown Corp as technology provider only';

function role_disclosure_clause(track) {
  const canonical = normalize(track);
  if (canonical === TRACK_RULE_506C) return RULE_506C_ROLE_CLAUSE;
  if (canonical === TRACK_REG_CF) return REG_CF_ROLE_CLAUSE;
  return '';
}

function track_for_cart_item(item) {
  if (!item) return null;
  return normalize(item.regulation_track) || normalize(item.type);
}

function cart_has_mixed_tracks(items) {
  if (!items || items.length < 2) return false;
  const set = new Set();
  for (const it of items) {
    const t = track_for_cart_item(it);
    if (t) set.add(t);
  }
  return set.size > 1;
}

module.exports = {
  TRACK_REG_CF,
  TRACK_RULE_506C,
  normalize,
  tracks_for_home,
  has_reg_cf,
  has_506c,
  is_mixed,
  is_only_506c,
  is_only_reg_cf,
  allowed_tracks,
  track_availability,
  open_tracks,
  is_track_open,
  close_date_for_track,
  track_status_label,
  track_availability_map,
  policy_for_track,
  user_facing_role_copy,
  role_disclosure_clause,
  REG_CF_ROLE_CLAUSE,
  RULE_506C_ROLE_CLAUSE,
  cart_has_mixed_tracks,
  track_for_cart_item,
};
